import React, { useEffect, useState } from "react";
import {
  FaImages,
  FaPlus,
  FaTrash,
  FaEdit,
  FaTimes,
  FaSave,
  FaImage,
} from "react-icons/fa";
import API, { getData, postData, putData, uploadUrl } from "../utils/api";

const AdminGallery = () => {
  const [gallery, setGallery] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const [formData, setFormData] = useState({
    title: "",
    category: "",
    image: "",
    imageFile: null,
  });

  const getImageSrc = (image) => {
    if (!image) return "";
    if (image.startsWith("http")) return image;
    if (image.startsWith("/uploads")) {
      return `${uploadUrl}${image.replace("/uploads", "")}`;
    }
    return `${uploadUrl}/${image}`;
  };

  const fetchGallery = async () => {
    try {
      setLoading(true);
      const data = await getData("/gallery");

      if (data.success) {
        setGallery(data.data || []);
      }
    } catch (error) {
      setMessage(error.response?.data?.message || "Gallery fetch failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGallery();
  }, []);

  const handleInput = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFormData((prev) => ({
      ...prev,
      imageFile: file,
      image: URL.createObjectURL(file),
    }));
  };

  const handleAdd = () => {
    setEditingId(null);
    setFormData({
      title: "",
      category: "",
      image: "",
      imageFile: null,
    });
    setShowForm(true);
  };

  const handleEdit = (item) => {
    setEditingId(item._id);
    setFormData({
      title: item.title || "",
      category: item.category || "",
      image: getImageSrc(item.image),
      imageFile: null,
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (
      !formData.title ||
      !formData.category ||
      (!editingId && !formData.imageFile)
    ) {
      alert("Please fill all fields.");
      return;
    }

    try {
      const body = new FormData();
      body.append("title", formData.title);
      body.append("category", formData.category);

      if (formData.imageFile) {
        body.append("image", formData.imageFile);
      }

      const data = editingId
        ? await putData(`/gallery/${editingId}`, body)
        : await postData("/gallery", body);

      if (!data.success) {
        throw new Error(data.message || "Save failed");
      }

      setShowForm(false);
      setEditingId(null);
      setFormData({
        title: "",
        category: "",
        image: "",
        imageFile: null,
      });

      setMessage(data.message || "Saved successfully");
      fetchGallery();
    } catch (error) {
      alert(
        error.response?.data?.message ||
          error.message ||
          "Something went wrong."
      );
    }
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      const response = await API.delete(`/gallery/${deleteId}`);
      const data = response.data;

      if (!data.success) {
        throw new Error(data.message || "Delete failed");
      }

      setShowDeleteModal(false);
      setDeleteId(null);
      setMessage(data.message || "Deleted successfully");
      fetchGallery();
    } catch (error) {
      alert(
        error.response?.data?.message ||
          error.message ||
          "Something went wrong."
      );
    }
  };

  const cancelDelete = () => {
    setDeleteId(null);
    setShowDeleteModal(false);
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl border border-[#D8E6FF] shadow-sm p-6 md:p-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h2 className="text-xl md:text-2xl font-semibold text-[#073B7A]">
              Gallery Management
            </h2>

            <p className="mt-1 text-sm text-gray-500">
              Upload and manage gallery images.
            </p>

            {message && (
              <p className="mt-2 text-sm font-semibold text-green-700">
                {message}
              </p>
            )}
          </div>

          <button
            onClick={handleAdd}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#0B4EA2] text-white text-sm font-medium hover:bg-[#F6C344] hover:text-[#073B7A] transition"
          >
            <FaPlus size={13} /> Add Image
          </button>
        </div>
      </div>

      {loading ? (
        <div className="bg-white rounded-2xl border border-[#D8E6FF] p-8 text-center text-[#073B7A] font-semibold">
          Loading gallery...
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {gallery.map((item) => (
            <div
              key={item._id}
              className="bg-white rounded-2xl border border-[#D8E6FF] shadow-sm overflow-hidden hover:shadow-md transition"
            >
              <div className="relative h-[240px] overflow-hidden">
                <img
                  src={getImageSrc(item.image)}
                  alt={item.title}
                  onError={(e) => {
                    e.currentTarget.src =
                      "https://placehold.co/600x400?text=No+Image";
                  }}
                  className="w-full h-full object-cover"
                />

                <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-[#073B7A]/70 to-transparent"></div>

                <div className="absolute top-3 right-3 flex items-center gap-2">
                  <button
                    onClick={() => handleEdit(item)}
                    className="w-10 h-10 rounded-xl bg-white text-[#073B7A] flex items-center justify-center shadow hover:bg-[#FFF3C4] transition"
                  >
                    <FaEdit size={14} />
                  </button>

                  <button
                    onClick={() => handleDelete(item._id)}
                    className="w-10 h-10 rounded-xl bg-red-500 text-white flex items-center justify-center shadow hover:bg-red-600 transition"
                  >
                    <FaTrash size={14} />
                  </button>
                </div>
              </div>

              <div className="p-5">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-[#FFF3C4] text-[#073B7A] flex items-center justify-center text-lg">
                    <FaImages />
                  </div>

                  <div>
                    <h3 className="font-semibold text-base text-[#073B7A]">
                      {item.title}
                    </h3>

                    <p className="text-xs text-gray-500 mt-1">
                      {item.category || "Gallery Image"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {gallery.length === 0 && (
            <div className="col-span-full bg-white border border-dashed border-[#D8E6FF] rounded-2xl p-10 text-center">
              <FaImage className="mx-auto text-5xl text-[#073B7A]" />

              <h3 className="mt-4 text-lg font-semibold text-[#073B7A]">
                No Images Found
              </h3>

              <p className="mt-2 text-sm text-gray-500">
                Upload your first gallery image.
              </p>
            </div>
          )}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-5">
          <div className="w-full max-w-2xl bg-white rounded-3xl p-8 border border-[#D8E6FF] shadow-xl">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-xl font-semibold text-[#073B7A]">
                  {editingId ? "Edit Gallery Image" : "Add Gallery Image"}
                </h3>

                <p className="text-sm text-gray-500 mt-1">
                  Upload gallery image, title and category.
                </p>
              </div>

              <button
                onClick={() => setShowForm(false)}
                className="w-10 h-10 rounded-xl bg-red-100 text-red-600 flex items-center justify-center hover:bg-red-600 hover:text-white transition"
              >
                <FaTimes />
              </button>
            </div>

            <div className="space-y-5">
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInput}
                placeholder="Enter image title"
                className="w-full px-4 py-3 rounded-xl border border-[#D8E6FF] outline-none text-sm focus:border-[#0B4EA2]"
              />

              <input
                type="text"
                name="category"
                value={formData.category}
                onChange={handleInput}
                placeholder="Enter category"
                className="w-full px-4 py-3 rounded-xl border border-[#D8E6FF] outline-none text-sm focus:border-[#0B4EA2]"
              />

              <label className="border-2 border-dashed border-[#D8E6FF] rounded-2xl p-8 flex flex-col items-center justify-center text-center cursor-pointer hover:bg-[#F8FBFF] transition">
                <FaImages className="text-5xl text-[#073B7A]" />

                <h4 className="mt-4 text-base font-medium text-[#073B7A]">
                  Click to Upload Image
                </h4>

                <p className="text-sm text-gray-500 mt-2">
                  JPG, PNG, JPEG supported
                </p>

                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleFile}
                />
              </label>

              {formData.image && (
                <img
                  src={formData.image}
                  alt="Preview"
                  className="w-full h-[250px] object-cover rounded-2xl border border-[#D8E6FF]"
                />
              )}
            </div>

            <div className="flex justify-end gap-3 mt-8">
              <button
                onClick={() => setShowForm(false)}
                className="px-6 py-3 rounded-xl border border-[#0B4EA2] text-[#0B4EA2] text-sm font-medium hover:bg-[#EEF5FF] transition"
              >
                Cancel
              </button>

              <button
                onClick={handleSave}
                className="px-6 py-3 rounded-xl bg-[#0B4EA2] text-white text-sm font-medium flex items-center gap-2 hover:bg-[#F6C344] hover:text-[#073B7A] transition"
              >
                <FaSave size={13} /> Save Image
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-white rounded-3xl border border-[#D8E6FF] shadow-xl overflow-hidden">
            <div className="p-6 text-center">
              <div className="w-20 h-20 rounded-full bg-red-100 text-red-600 flex items-center justify-center mx-auto">
                <FaTrash className="text-3xl" />
              </div>

              <h3 className="mt-5 text-xl font-semibold text-[#073B7A]">
                Delete Image?
              </h3>

              <p className="mt-2 text-sm text-gray-500 leading-6">
                This image will be permanently deleted.
              </p>
            </div>

            <div className="flex items-center gap-3 p-5 pt-0">
              <button
                onClick={cancelDelete}
                className="flex-1 h-11 rounded-xl border border-[#0B4EA2] text-[#0B4EA2] text-sm font-medium hover:bg-[#EEF5FF] transition"
              >
                Cancel
              </button>

              <button
                onClick={confirmDelete}
                className="flex-1 h-11 rounded-xl bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminGallery;
